import { useContext, useMemo } from 'react';
import { isNil } from 'lodash';
import type { Portfolio } from 'venn-api';
import { UserContext } from '../../../../contexts';
import { getContextMenuItems } from '../../../../utils/getContextMenuItems';
import { MultiPortfolioReviewContext } from '../components/common/MultiPortfolioReviewContext';

export const useOwnershipAssignment = () => {
  const { profileSettings } = useContext(UserContext);

  const {
    data: { parsedResults, excludedParsedData },
    actions: { updatePortfolio },
  } = useContext(MultiPortfolioReviewContext);

  const excludedIds = useMemo(
    () => new Set(excludedParsedData.map((p) => p.parsedPortfolio?.id ?? p.parsedPortfolio?.name)),
    [excludedParsedData],
  );

  const availableContexts = useMemo(
    () =>
      getContextMenuItems(profileSettings?.availableContexts)?.map((item) => ({
        value: item.value,
        label: item.label,
      })) || [],
    [profileSettings?.availableContexts],
  );

  const { existingPortfolios, newPortfolios } = useMemo(() => {
    const existingPortfolios: Portfolio[] = [];
    const newPortfolios: Portfolio[] = [];

    parsedResults.forEach((result) => {
      if (result.parsedPortfolio) {
        const idOrName = result.parsedPortfolio.id ?? result.parsedPortfolio.name;
        if (excludedIds.has(idOrName)) return;

        const isNew = isNil(result.parsedPortfolio.id);
        if (isNew) {
          newPortfolios.push(result.parsedPortfolio);
        } else {
          existingPortfolios.push(result.parsedPortfolio);
        }
      }
    });

    return { existingPortfolios, newPortfolios };
  }, [parsedResults, excludedIds]);

  const handleOwnershipChange = (portfolio: Portfolio, ownerContextId: string) => {
    const index = parsedResults.findIndex((result) => {
      if (!result.parsedPortfolio) return false;

      if (portfolio.id && result.parsedPortfolio.id) {
        return result.parsedPortfolio.id === portfolio.id;
      }

      return result.parsedPortfolio.name === portfolio.name;
    });

    if (index !== -1 && parsedResults[index]?.parsedPortfolio) {
      updatePortfolio(index, {
        ...parsedResults[index].parsedPortfolio,
        ownerContextId,
      });
    }
  };

  const allAssigned = useMemo(() => {
    return [...existingPortfolios, ...newPortfolios].every((portfolio) => portfolio.ownerContextId);
  }, [existingPortfolios, newPortfolios]);

  return {
    availableContexts,
    allAssigned,
    existingPortfolios,
    newPortfolios,
    handleOwnershipChange,
    parsedResults,
  };
};
